'use client';

import { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import logger from '@/lib/logger';

// Key to track auto-reload attempts in sessionStorage
const RELOAD_KEY = 'app_chunk_reload_attempt';
const MAX_AUTO_RELOADS = 2;

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    // Log the error using centralized logger
    logger.error('Global Error Handler', error.message, {
      stack: error.stack,
      digest: error.digest,
    });

    // Check if this is a ChunkLoadError (stale deployment)
    const isChunkError = error.name === 'ChunkLoadError' ||
                         error.message?.includes('Loading chunk') ||
                         error.message?.includes('ChunkLoadError');

    if (isChunkError) {
      logger.debug('GlobalErrorHandler', 'Detected ChunkLoadError - checking auto-reload');
      handleChunkLoadError();
    }
  }, [error]);

  const handleChunkLoadError = () => {
    // Check how many times we've auto-reloaded
    const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);

    if (reloadCount < MAX_AUTO_RELOADS) {
      logger.debug('GlobalErrorHandler', 'Auto-reloading page', { attempt: reloadCount + 1, maxReloads: MAX_AUTO_RELOADS });
      sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));

      // Small delay to prevent rapid reload loops
      setIsReloading(true);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      logger.debug('GlobalErrorHandler', 'Max auto-reloads reached, showing manual retry UI');
      // Clear the counter so next session can try again
      sessionStorage.removeItem(RELOAD_KEY);
    }
  };

  const handleRetry = () => {
    // Clear reload counter and reload the page
    sessionStorage.removeItem(RELOAD_KEY);
    setIsReloading(true);
    window.location.reload();
  };

  // Check if this is a chunk error for special messaging
  const isChunkError = error.name === 'ChunkLoadError' ||
                       error.message?.includes('Loading chunk') ||
                       error.message?.includes('ChunkLoadError');

  // Show reloading state
  if (isReloading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="max-w-md p-8 bg-slate-800 rounded-lg shadow-xl text-center">
          <RefreshCw className="w-12 h-12 text-blue-400 mb-4 mx-auto animate-spin" />
          <h2 className="text-xl font-semibold mb-2">Updating application...</h2>
          <p className="text-gray-400">
            A newer version is available. The page will reload automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
      <div className="max-w-md p-8 bg-slate-800 rounded-lg shadow-xl text-center">
        {isChunkError ? (
          <>
            <RefreshCw className="w-12 h-12 text-yellow-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold mb-4 text-yellow-400">Update Required</h2>
            <p className="text-gray-300 mb-4">
              The application has been updated. Please reload the page to continue.
            </p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </button>
          </>
        ) : (
          <>
            <AlertTriangle className="w-12 h-12 text-blue-400 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold mb-4 text-blue-400">Something went wrong!</h2>
            <p className="text-gray-300 mb-4">{error.message}</p>
            <button
              onClick={reset}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
